import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config';
import type { User, UserRoleAssignment } from '@/types';
import { handleFirebaseError } from '../../utils';
import { CHUNK_CONCURRENCY, chunkIds, forEachLimited } from '../batchedIn';

export interface OrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  role: 'customer' | 'manager' | 'examiner' | 'instructor';
  addedAt: Date;
  addedBy: string;
}

export async function getOrganizationMembers(organizationId: string): Promise<(OrganizationMember & User)[]> {
  try {
    const membersQuery = query(
      collection(db, 'organizationMembers'),
      where('organizationId', '==', organizationId)
    );
    
    const membersSnapshot = await getDocs(membersQuery);
    // addedAt as a Date, as the type says: it stayed a Timestamp, so the
    // manager statistics' «new beneficiaries per month» (addedAt instanceof
    // Date) counted nobody.
    const members = membersSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        addedAt: typeof data.addedAt?.toDate === 'function' ? data.addedAt.toDate() : data.addedAt,
      };
    }) as OrganizationMember[];

    // Users for everyone at once, 30 per `in` query (#117), instead of one
    // query per member. Each member gets the first doc of their `id ==`
    // query, in document-id order, as the per-member query returned it.
    const usersById = new Map<string, User>();
    await forEachLimited(chunkIds(members.map((member) => member.userId)), CHUNK_CONCURRENCY, async (part) => {
      const snapshot = await getDocs(query(collection(db, 'users'), where('id', 'in', part)));
      snapshot.docs.forEach((d) => {
        const data = d.data() as User;
        if (!usersById.has(data.id)) usersById.set(data.id, data);
      });
    });

    const membersWithDetails = members.map((member) => {
      const userData = usersById.get(member.userId);
      return userData ? { ...member, ...userData } : null;
    });

    return membersWithDetails.filter((m): m is OrganizationMember & User => 
      m !== null && 
      m.roles?.some((r: UserRoleAssignment) => r.role === 'customer' && r.isActive) &&
      m.role !== 'manager' // Exclude organization managers from the customer list
    );
  } catch (error) {
    handleFirebaseError(error, 'Error fetching organization members');
  }
}